import { z } from 'zod';
import { WidgetApi, WidgetApiDataSchema, type WidgetApiDataType } from './WidgetApi';
import {
    availableWidgets,
    type AvailableWidgetType,
    WidgetSettingsOptionsSchema,
} from '../../components/Dashboard/availableWidgets';
import type { QueriesType } from '../../components/telematics/types';
import { WidgetEngine } from './WidgetEngine';
import { StatusApiDataRequestSchema } from './WidgetOneApi';
import { openModal } from 'svelte-modals';
import ModalWidgetSettings from './ModalWidgetSettings.svelte';
import { get } from 'svelte/store';

export const DashboardComponentSchema = z.object({
    id: z.string(),
    order: z.number(),
    componentId: z.number(),
    settings: StatusApiDataRequestSchema,
    component: z.any(),
    settingsOptions: WidgetSettingsOptionsSchema,
    we: z.any().describe('WidgetEngine'),
});
export type DashboardComponentType = z.infer<typeof DashboardComponentSchema>;

export class WidgetManager {
    private components: DashboardComponentType[] = [];
    public mainQuery: QueriesType = {};
    private dashboardApi: WidgetApi;
    private _autosave: boolean = false;

    constructor(dashboardApi: WidgetApi) {
        this.dashboardApi = dashboardApi;
    }

    setAutosave(autosave: boolean) {
        this._autosave = autosave;
    }

    getApi(): WidgetApi {
        return this.dashboardApi;
    }

    setApi(api: WidgetApi) {
        this.dashboardApi = api;
    }

    async load(): Promise<void> {
        let data = await this.dashboardApi.load();
        data = WidgetApiDataSchema.parse(data); // fix for: https://lupus.automapa.pl/organizations/automapa/issues/4033/?environment=lastmile-demo.automapa.pl&project=12&statsPeriod=7d
        this.importData(data);
    }

    async save(forceToSave: boolean = false): Promise<boolean> {
        if (forceToSave || this._autosave) {
            console.log('WidgetManager, saving...', this.mainQuery);
            return this.dashboardApi.save({
                mainQuery: this.mainQuery,
                components: this.components.map(component => ({
                    id: component.id,
                    order: component.order,
                    componentId: component.componentId,
                    settings: component.settings,
                })),
            });
        } else {
            console.log('WidgetManager, save is blocked', this.mainQuery);
            return false;
        }
    }

    getComponentIds(): string[] {
        return this.components.map(component => component.id);
    }

    importData(data: WidgetApiDataType): void {
        console.log('WidgetApi, Import data', data);
        this.components = [];
        this.mainQuery = data.mainQuery;
        data.components.forEach(component => {
            let selectedWidget = get(availableWidgets).find(
                widget => widget.id === component.componentId
            );
            if (selectedWidget) {
                try {
                    this.components.push({
                        id: component.id ?? Math.random().toString(36),
                        order: component.order,
                        componentId: component.componentId,
                        settings: selectedWidget?.settingsSchema.parse(component.settings),
                        component: selectedWidget?.component,
                        settingsOptions: selectedWidget?.settingsOptions,
                        we: new WidgetEngine(selectedWidget.widgetName, selectedWidget.title),
                    });
                } catch (e) {
                    console.error('Error parsing settings', e);
                }
            } else {
                console.error('Widget not found', component.componentId);
            }
        });
        console.log('imported components', this.components);
    }

    public async addWidget(selectedWidget: AvailableWidgetType): Promise<void> {
        this.components.push({
            id: Math.random().toString(36),
            order: this.components.length + 1,
            settings: selectedWidget?.settingsSchema.parse(selectedWidget.defaultSettings),
            componentId: selectedWidget.id,
            component: selectedWidget.component,
            settingsOptions: selectedWidget.settingsOptions,
            we: new WidgetEngine(selectedWidget.widgetName, selectedWidget.title),
        });
        if (this._autosave) {
            await this.save();
        }
    }

    public getComponents(): DashboardComponentType[] {
        return this.components;
    }

    public removeComponent(componentId: string): void {
        this.components = this.components.filter(component => component.id !== componentId);
        this.orderComponentsByOrder();
        if (this._autosave) {
            this.save();
        }
    }

    public async setSettings(id: string, settings: any) {
        const component = this.components.find(component => component.id === id);
        if (component) {
            component.settings = JSON.parse(JSON.stringify(settings));
            if (this._autosave) {
                await this.save();
            }
        }
    }

    public getSettings(id: string): any {
        const component = this.components.find(component => component.id === id);
        if (component) {
            return component.settings;
        }
        return {};
    }

    public getDashboardItem(id: string): DashboardComponentType | undefined {
        return this.components.find(component => component.id === id);
    }

    public setOrder(orderedId: string[]) {
        this.components = this.components.map(component => {
            const index = orderedId.indexOf(component.id);
            if (index > -1) {
                component.order = index + 1;
            }
            return component;
        });
        this.orderComponentsByOrder();
        if (this._autosave) {
            this.save();
        }
    }

    public orderComponentsByOrder() {
        this.components.sort((a, b) => a.order - b.order);
    }

    public reloadAllWidgets() {
        this.components.forEach(component => {
            component.we.reload();
        });
    }

    public onSettingsClick(dashboardItem: DashboardComponentType, afterRemove: () => void) {
        console.log('onSettingsClick', dashboardItem?.settings);
        if (!dashboardItem) {
            console.warn('dashboardItem is not defined');
            return;
        }
        openModal(ModalWidgetSettings, {
            onRemove: () => {
                console.log('onRemove', dashboardItem);
                this.removeComponent(dashboardItem.id);
                if (this._autosave) {
                    this.save().then(() => {
                        afterRemove();
                    });
                } else {
                    afterRemove();
                }
            },
            onSave: settings => {
                console.log('onSave', dashboardItem, settings);
                this.setSettings(dashboardItem.id, settings).then(() => {
                    dashboardItem.we.reload();
                });
            },
            settings: dashboardItem.settings,
            // componentSettings: componentSettings,
            widgetManager: this,
            dashboardItem,
        });
    }

    public hasAnyFilterActive(dashboardItem: DashboardComponentType): boolean {
        console.log('hasAnyFilterActive', dashboardItem.settings);
        return Object.keys(dashboardItem.settings.query).length > 0;
    }
}
